import { useCallback, useEffect, useState } from 'react'

import { Form, Formik } from 'formik'
import {
  useEmailContactSearch,
  useMobileContactSearch,
} from 'hooks/useEmailContactSearch'
import { usePMSPropertySearch } from 'hooks/usePMSPropertySearch'
import { useTenancyTransferMutation } from 'hooks/useTenancyTransferMutation'
import { isValidNumber } from 'libphonenumber-js'
import styled from 'styled-components'

import { Box, Modal, Select } from 'app/components/design-system-components'
import {
  AutocompleteField,
  AutocompleteTelField,
  DateTimeField,
  InputField,
  TextArea,
} from 'app/components/design-system-components/inputs'
import { Text } from 'app/components/design-system-components/typography'
import { formValidationBuilder } from 'app/forms/utils/formValidationBuilder'
import { debounce, STANDARD_TIMEOUT } from 'app/helpers/debounce'
import { StyledFieldWrapper } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/styled-form-component'
import * as snugNotifier from 'app/services/snugNotifier'
import TenancyTransferAlert from 'app/shared_components/navbar/components/TenancyTransferAlert'
import {
  formFields,
  FormValues,
  STEP_1_FIELDS,
  STEP_2_FIELDS,
  STEP_3_FIELDS,
  STEPS,
} from 'app/shared_components/navbar/components/tenancyTransferConstants'
import {
  normalizeStateToShortForm,
  shortAustralianStates,
} from 'app/utils/address/helpers'

type StartTenancyTransferModalProps = {
  toggleModal: () => void
  isOpen: boolean
  teamGUID?: string
}

const StyledProgressBar = styled.div`
  width: 100%;
  height: ${({ theme }) => theme.height[5]}px;
  background-color: ${({ theme }) => theme.colors.grey200};
  margin-bottom: ${({ theme }) => theme.space[8]}px;
  overflow: hidden;
`

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.blue500};
  width: ${({ progress }) => progress}%;
  transition: width 0.3s ease;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colors.grey600};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.space[4]}px;
`

const InputContainer = styled(Box)`
  margin-bottom: ${({ theme }) => theme.space[7]}px;
  margin-top: ${({ theme }) => theme.space[7]}px;
`

const CustomStyledFieldWrapper = styled(StyledFieldWrapper)`
  width: 100%;
`

const MobileStyledFieldWrapper = styled(CustomStyledFieldWrapper)`
  /* Fix country dropdown to be a vertical list */
  .country-list li.country {
    display: block !important;
    width: 100% !important;
    clear: both !important;
  }
`

const STEP_CONTAINER_HEIGHT = 300
const STEP2_CONTAINER_HEIGHT = 500
const SELECT_HEIGHT = 48

// Shared CSS for select elements
const selectStyles = `
  select {
    border: 1px solid #d1d5db;
    height: ${SELECT_HEIGHT}px;
    outline: none;
  }
`

const StepContainer = styled(Box)`
  min-height: ${STEP_CONTAINER_HEIGHT}px;
  ${selectStyles}
`

const Step2Container = styled(Box)`
  height: ${STEP2_CONTAINER_HEIGHT}px;
  overflow-y: auto;
  ${selectStyles}
`

const EmailOptionContainer = styled.div<{ isLast?: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: ${({ isLast }) => (isLast ? 'none' : '1px solid #e5e5e5')};
`

// Custom option renderer for email dropdown that shows full name + email
const EmailOptionRenderer = ({ option, onClick, isLast, ...props }: any) => {
  return (
    <EmailOptionContainer onClick={onClick} isLast={isLast} {...props}>
      {option.optionMeta.dropdownDisplayText || option.optionMeta.displayText}
    </EmailOptionContainer>
  )
}

// Field validation function
const fieldValidation = (fieldName: string, val: any, _values: any) => {
  switch (fieldName) {
    case formFields.streetNumber:
      return {
        required: !val && 'Street number is required',
      }
    case formFields.streetName:
      return {
        required: !val && 'Street name is required',
      }
    case formFields.suburb:
      return {
        required: !val && 'Suburb is required',
      }
    case formFields.state:
      return {
        required: !val && 'State is required',
      }
    case formFields.postcode: {
      return {
        required: !val && 'Postcode is required',
        pattern: val && !/^\d{4}$/.test(val) && 'Postcode must be 4 digits',
      }
    }
    case formFields.leaseStartDate: {
      // Accept both string format DD/MM/YYYY and valid Date objects from date picker
      const isStringValue = typeof val === 'string'
      const isValidStringFormat =
        isStringValue && /^\d{2}\/\d{2}\/\d{4}$/.test(val)

      return {
        required: !val && 'Lease start date is required',
        pattern:
          val &&
          isStringValue &&
          !isValidStringFormat &&
          'Lease Start Date not in DD/MM/YYYY format.',
      }
    }
    case formFields.weeklyRent: {
      const numericValue = val ? parseFloat(val.replace(/[$,]/g, '')) : 0
      return {
        required: !val && 'Weekly rent is required',
        pattern:
          val &&
          !/^\$?\d+(\.\d{2})?$/.test(val) &&
          'Please enter a valid amount',
        minimum:
          val && numericValue <= 0 && 'Weekly rent not greater than zero.',
      }
    }
    case formFields.email: {
      // Handle both string and object (when selected from autocomplete)
      const emailValue =
        val && typeof val === 'object' ? (val as any).option?.email : val
      return {
        pattern:
          emailValue &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue) &&
          'Please enter a valid email address',
      }
    }
    case formFields.mobile: {
      // Handle both string and object (when selected from autocomplete)
      const mobileValue =
        val && typeof val === 'object' ? (val as any).option?.phone : val
      return {
        pattern:
          mobileValue &&
          !isValidNumber(mobileValue) &&
          'Please enter a valid mobile number',
      }
    }
    default:
      return {}
  }
}

// Build form validation
const formValidation = formValidationBuilder(fieldValidation)

// Helper to check if current step has errors
const hasCurrentStepErrors = (values: FormValues, currentStep: number) => {
  switch (currentStep) {
    case 1:
      return !validateStep1(values)
    case 2:
      return !validateStep2(values)
    case 3:
      return !validateStep3(values)
    default:
      return false
  }
}

// Step-specific field validation
const validateStep1 = (values: FormValues) => {
  const step1Values = STEP_1_FIELDS.reduce((acc, field) => {
    acc[field] = values[field as keyof FormValues]
    return acc
  }, {} as any)

  const errors = formValidation(step1Values)
  return Object.keys(errors).length === 0
}

const validateStep2 = (values: FormValues) => {
  const step2Values = STEP_2_FIELDS.reduce((acc, field) => {
    acc[field] = values[field as keyof FormValues]
    return acc
  }, {} as any)

  const errors = formValidation(step2Values)
  return Object.keys(errors).length === 0
}

const validateStep3 = (values: FormValues) => {
  const step3Values = STEP_3_FIELDS.reduce((acc, field) => {
    acc[field] = values[field as keyof FormValues]
    return acc
  }, {} as any)

  const errors = formValidation(step3Values)

  // Check if at least one contact method is provided
  const emailValue =
    values.email && typeof values.email === 'object'
      ? (values.email as any).option?.email
      : values.email
  const hasEmail = emailValue && emailValue.trim().length > 0
  const hasMobile = values.mobile && values.mobile.trim().length > 0

  if (!hasEmail && !hasMobile) {
    return false // Neither email nor mobile provided
  }

  return Object.keys(errors).length === 0
}

function StartTenancyTransferModal({
  toggleModal,
  isOpen,
  teamGUID,
}: StartTenancyTransferModalProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [addressSearchQuery, setAddressSearchQuery] = useState('')
  const [emailSearchQuery, setEmailSearchQuery] = useState('')
  const [mobileSearchQuery, setMobileSearchQuery] = useState('')
  const [formKey, setFormKey] = useState(0) // Key to force Formik re-render

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1)
      setAddressSearchQuery('')
      setEmailSearchQuery('')
      setMobileSearchQuery('')
      setFormKey((prev) => prev + 1)
    }
  }, [isOpen])

  // Transform states into options for Select component
  const stateOptions = shortAustralianStates.map((state) => ({
    id: state,
    name: state,
  }))

  const tenancyTransferMutation = useTenancyTransferMutation(teamGUID || '', {
    onSuccess: () => {
      snugNotifier.success('Tenancy transfer request submitted successfully!')
      toggleModal()
    },
  })

  // Search for PMS properties with debounced query
  const { data: pmsPropertiesData, isLoading: isLoadingProperties } =
    usePMSPropertySearch(teamGUID || '', addressSearchQuery)

  // Search for email contacts with debounced query
  const { data: emailContactsData, isLoading: isLoadingEmails } =
    useEmailContactSearch(teamGUID || '', emailSearchQuery)

  // Search for mobile contacts with debounced query
  const { data: mobileContactsData, isLoading: isLoadingMobiles } =
    useMobileContactSearch(teamGUID || '', mobileSearchQuery)

  // Debounced search function for address
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setAddressSearchQuery(query)
    }, STANDARD_TIMEOUT),
    [STANDARD_TIMEOUT],
  )

  // Debounced search function for email
  const debouncedEmailSearch = useCallback(
    debounce((query: string) => {
      setEmailSearchQuery(query)
    }, STANDARD_TIMEOUT),
    [STANDARD_TIMEOUT],
  )

  // Debounced search function for mobile
  const debouncedMobileSearch = useCallback(
    debounce((query: string) => {
      setMobileSearchQuery(query)
    }, STANDARD_TIMEOUT),
    [STANDARD_TIMEOUT],
  )

  // Process properties data into autocomplete options
  const propertyOptions = (pmsPropertiesData as any)?.data
    ? (pmsPropertiesData as any).data.map((property: any, index: number) => ({
        optionMeta: {
          id: property.id?.toString() || property.guid || `property-${index}`,
          displayText: [
            property.unitNumber,
            property.streetNumber,
            property.streetName,
            property.suburb,
            property.state,
            property.postcode,
          ]
            .filter(Boolean)
            .join(', '),
        },
        option: property,
      }))
    : []

  // Process email contacts data into autocomplete options
  const emailOptions = (emailContactsData as any)?.data
    ? (emailContactsData as any).data
        .filter((contact: any) => contact.email && contact.email.trim()) // Only include contacts with valid email addresses
        .map((contact: any, index: number) => {
          // Format display text for dropdown as "First name Lastname (email)"
          const nameDisplay = [
            contact.firstName?.trim(),
            contact.lastName?.trim(),
          ]
            .filter(Boolean)
            .join(' ')

          const dropdownDisplayText = nameDisplay
            ? `${nameDisplay} (${contact.email})`
            : contact.email || ''

          return {
            optionMeta: {
              id: contact.guidID || contact.id?.toString() || `email-${index}`, // Fallback to ensure unique ID
              displayText: contact.email || '', // Show only email in input field after selection
              dropdownDisplayText: dropdownDisplayText, // Show full name + email in dropdown
            },
            option: contact,
          }
        })
    : []

  // Process mobile contacts data into autocomplete options
  const mobileOptions = (mobileContactsData as any)?.data
    ? (mobileContactsData as any).data
        .filter((contact: any) => contact.phone && contact.phone.trim()) // Only include contacts with valid phone numbers
        .map((contact: any, index: number) => {
          // Format display text as "First name Lastname (number)"
          const displayText = [
            contact.firstName?.trim(),
            contact.lastName?.trim(),
          ]
            .filter(Boolean)
            .join(' ')

          const formattedDisplayText = displayText
            ? `${displayText} (${contact.phone})`
            : contact.phone || ''

          return {
            optionMeta: {
              id:
                contact.guidID ||
                contact.id?.toString() ||
                contact.phone ||
                `mobile-${index}`, // Fallback to ensure unique ID
              displayText: formattedDisplayText,
            },
            option: contact,
          }
        })
    : []

  // Handle address search input
  const handleAddressSearch = (query: string) => {
    debouncedSearch(query)
  }

  // Handle email search input
  const handleEmailSearch = (query: string) => {
    debouncedEmailSearch(query)
  }

  // Handle mobile search input
  const handleMobileSearch = (query: string) => {
    debouncedMobileSearch(query)
  }

  // Function to auto-populate fields from selected property
  const handlePropertySelect = (property: any, setFieldValue: any) => {
    setFieldValue(formFields.unitNumber, property.unitNumber?.toString() || '')
    setFieldValue(
      formFields.streetNumber,
      property.streetNumber?.toString() || '',
    )
    setFieldValue(formFields.streetName, property.streetName || '')
    setFieldValue(formFields.suburb, property.suburb || '')
    setFieldValue(
      formFields.state,
      normalizeStateToShortForm(property.state || ''),
    )
    setFieldValue(formFields.postcode, property.postcode?.toString() || '')
  }

  const initialValues: FormValues = {
    address: '',
    unitNumber: '',
    streetNumber: '',
    streetName: '',
    suburb: '',
    state: 'NSW',
    postcode: '',
    leaseStartDate: '',
    weeklyRent: '',
    email: '',
    mobile: '',
    firstName: '',
    lastName: '',
    message: '',
  }

  const handleNext = (values: FormValues) => {
    let isStepValid = false

    switch (currentStep) {
      case 1:
        isStepValid = validateStep1(values)
        break
      case 2:
        isStepValid = validateStep2(values)
        break
      case 3:
        isStepValid = validateStep3(values)
        break
      default:
        isStepValid = true
    }

    if (isStepValid && currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = (values: FormValues) => {
    if (!teamGUID) {
      snugNotifier.error('Team ID is required for tenancy transfer')
      return
    }

    // Transform the date from DateTimeField format to DD/MM/YYYY
    let formattedDate = values.leaseStartDate
    if (values.leaseStartDate) {
      // Handle different date formats that might come from DateTimeField
      const date = new Date(values.leaseStartDate)
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        formattedDate = `${day}/${month}/${year}`
      }
    }

    // Handle address format - if autocomplete was used, extract the display text
    let addressForSubmission = ''
    if (values.address && typeof values.address === 'object') {
      // Address was selected from autocomplete
      addressForSubmission = values.address.optionMeta?.displayText || ''
    } else if (typeof values.address === 'string') {
      // Address was typed manually in the autocomplete field
      addressForSubmission = values.address
    }

    // Handle email format - if autocomplete was used, extract the email
    let emailForSubmission = ''
    if (values.email && typeof values.email === 'object') {
      // Email was selected from autocomplete
      emailForSubmission = (values.email as any).option?.email || ''
    } else if (typeof values.email === 'string') {
      // Email was typed manually in the autocomplete field
      emailForSubmission = values.email
    }

    const transferData: any = {
      ...values,
      address: addressForSubmission,
      email: emailForSubmission,
      firstName: values.firstName || '',
      lastName: values.lastName || '',
      leaseStartDate: formattedDate,
    }

    tenancyTransferMutation.mutate(transferData)
  }

  const getCurrentProgress = () => {
    return STEPS.find((step) => step.id === currentStep)?.progress || 0
  }

  const renderStep1 = (
    values: FormValues,
    _errors: any,
    _touched: any,
    setFieldValue: any,
  ) => (
    <StepContainer>
      <TenancyTransferAlert />

      <InputContainer>
        <CustomStyledFieldWrapper
          id="address"
          name="address"
          label="Address (optional)"
        >
          <AutocompleteField
            name="address"
            placeholder="Search for address"
            options={propertyOptions}
            onChangeText={handleAddressSearch}
            isLoading={isLoadingProperties && addressSearchQuery.length >= 2}
            changeHandler={(optionConfig: any) => {
              setFieldValue('address', optionConfig)
              // Auto-populate other fields if a property is selected
              if (optionConfig && optionConfig.option) {
                handlePropertySelect(optionConfig.option, setFieldValue)
              }
            }}
          />
        </CustomStyledFieldWrapper>
      </InputContainer>

      <Text variant="body" color="grey600" mt={8} mb={4}>
        Or enter an address
      </Text>

      <InputContainer
        display="flex"
        flexDirection={['column', 'column', 'row']}
        gap={[3, 3, 4]}
      >
        <Box width={['100%', '100%', '50%']}>
          <CustomStyledFieldWrapper
            id="unitNumber"
            name="unitNumber"
            label="Unit Number (optional)"
          >
            <InputField
              name="unitNumber"
              inputProps={{
                placeholder: 'e.g. 1',
                value: values.unitNumber,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('unitNumber', e.target.value),
              }}
            />
          </CustomStyledFieldWrapper>
        </Box>

        <Box width={['100%', '100%', '50%']} ml={[0, 0, 5]} mt={[7, 7, 0]}>
          <CustomStyledFieldWrapper
            id="streetNumber"
            name="streetNumber"
            label="Street Number"
            required
          >
            <InputField
              name="streetNumber"
              inputProps={{
                placeholder: 'e.g. 123',
                value: values.streetNumber,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('streetNumber', e.target.value),
              }}
            />
          </CustomStyledFieldWrapper>
        </Box>
      </InputContainer>

      <InputContainer
        display="flex"
        flexDirection={['column', 'column', 'row']}
        gap={[3, 3, 4]}
      >
        <Box width={['100%', '100%', '50%']}>
          <CustomStyledFieldWrapper
            id="streetName"
            name="streetName"
            label="Street Name"
            required
          >
            <InputField
              name="streetName"
              inputProps={{
                placeholder: 'e.g. Main Street',
                value: values.streetName,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('streetName', e.target.value),
              }}
            />
          </CustomStyledFieldWrapper>
        </Box>

        <Box width={['100%', '100%', '50%']} ml={[0, 0, 5]} mt={[7, 7, 0]}>
          <CustomStyledFieldWrapper
            id="suburb"
            name="suburb"
            label="Suburb"
            required
          >
            <InputField
              name="suburb"
              inputProps={{
                placeholder: 'e.g. Sydney',
                value: values.suburb,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('suburb', e.target.value),
              }}
            />
          </CustomStyledFieldWrapper>
        </Box>
      </InputContainer>

      <InputContainer
        display="flex"
        flexDirection={['column', 'column', 'row']}
        gap={[3, 3, 4]}
      >
        <Box width={['100%', '100%', '50%']}>
          <CustomStyledFieldWrapper
            id="state"
            name="state"
            label="State"
            required
          >
            <Select
              id="state"
              selectName="state"
              options={stateOptions}
              value={normalizeStateToShortForm(values.state)}
              label="Select State"
              labelText=""
              showSelectLabel={true}
              validate={undefined}
              onSelectChange={({ selectedIndex }: { selectedIndex: any }) => {
                if (selectedIndex > 0) {
                  const selectedState = stateOptions[selectedIndex - 1]?.id
                  if (selectedState) {
                    setFieldValue('state', selectedState)
                  }
                }
              }}
            />
          </CustomStyledFieldWrapper>
        </Box>

        <Box width={['100%', '100%', '50%']} ml={[0, 0, 5]} mt={[7, 7, 0]}>
          <CustomStyledFieldWrapper
            id="postcode"
            name="postcode"
            label="Postcode"
            required
          >
            <InputField
              name="postcode"
              inputProps={{
                placeholder: 'e.g. 2000',
                value: values.postcode,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('postcode', e.target.value),
              }}
            />
          </CustomStyledFieldWrapper>
        </Box>
      </InputContainer>
    </StepContainer>
  )

  const renderStep2 = (
    values: FormValues,
    _errors: any,
    _touched: any,
    setFieldValue: any,
  ) => (
    <Step2Container>
      <TenancyTransferAlert />

      <InputContainer>
        <CustomStyledFieldWrapper
          id="leaseStartDate"
          name="leaseStartDate"
          label="Lease start date"
          required
        >
          <DateTimeField
            name="leaseStartDate"
            placeholder="dd/mm/yyyy"
            dateFormat="DD/MM/YYYY"
            timeFormat={false}
            value={values.leaseStartDate}
            changeHandler={(value: any) =>
              setFieldValue('leaseStartDate', value)
            }
          />
        </CustomStyledFieldWrapper>
      </InputContainer>

      <InputContainer>
        <CustomStyledFieldWrapper
          id="weeklyRent"
          name="weeklyRent"
          label="Weekly rent"
          required
        >
          <InputField
            name="weeklyRent"
            inputProps={{
              placeholder: '$',
              value: values.weeklyRent,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('weeklyRent', e.target.value),
            }}
          />
        </CustomStyledFieldWrapper>
      </InputContainer>
    </Step2Container>
  )

  const renderStep3 = (
    values: FormValues,
    _errors: any,
    _touched: any,
    setFieldValue: any,
  ) => (
    <StepContainer>
      <TenancyTransferAlert />

      <StyledText>Add an email address or mobile number</StyledText>

      <InputContainer>
        <CustomStyledFieldWrapper id="email" name="email" label="Email">
          <AutocompleteField
            name="email"
            placeholder="e.g. john.citizen@example.com"
            options={emailOptions}
            OptionRenderCmp={EmailOptionRenderer}
            onChangeText={(query: string) => {
              handleEmailSearch(query)
              // Convert manually typed email to object structure to maintain consistency
              // This ensures the email value is retained when navigating back and forth
              if (query && query.trim()) {
                const emailObject = {
                  optionMeta: {
                    id: `manual-${query}`,
                    displayText: query,
                  },
                  option: {
                    email: query,
                  },
                }
                setFieldValue('email', emailObject)
              } else {
                setFieldValue('email', '')
              }
            }}
            isLoading={isLoadingEmails && emailSearchQuery.length >= 2}
            changeHandler={(optionConfig: any) => {
              if (optionConfig && optionConfig.option) {
                // Set email from selected contact - store the full option object
                setFieldValue('email', optionConfig)
                setFieldValue('mobile', optionConfig?.option?.phone || '')
                setFieldValue(
                  'firstName',
                  optionConfig?.option?.firstName || '',
                )
                setFieldValue('lastName', optionConfig?.option?.lastName || '')
              } else if (optionConfig === null) {
                // Allow clearing the field
                setFieldValue('email', '')
              }
              // Note: Manual text input is handled by onChangeText, not changeHandler
            }}
          />
        </CustomStyledFieldWrapper>
      </InputContainer>

      <InputContainer>
        <MobileStyledFieldWrapper id="mobile" name="mobile" label="Mobile">
          <AutocompleteTelField
            name="mobile"
            placeholder="e.g. +61 400 000 000"
            options={mobileOptions}
            onChangeText={(query: string) => {
              handleMobileSearch(query)
              // Set the field value - the AutocompleteTelField will pass this to TelField
              setFieldValue('mobile', query)
            }}
            // Pass through additional props to enable country detection
            international={true}
            isLoading={isLoadingMobiles && mobileSearchQuery.length >= 2}
            changeHandler={(optionConfig: any) => {
              if (optionConfig && optionConfig.option) {
                // Set mobile from selected contact (using phone field)
                const phoneNumber = optionConfig.option.phone
                setFieldValue('mobile', phoneNumber)

                // Force a re-validation after the component state updates
                setTimeout(() => {
                  setFieldValue('mobile', phoneNumber)
                }, 0)

                const emailOptionConfig = {
                  ...optionConfig,
                  option: {
                    ...optionConfig.option,
                    email: optionConfig?.option?.email || '',
                  },
                  optionMeta: {
                    ...optionConfig.optionMeta,
                    displayText: optionConfig?.option?.email || '',
                  },
                }
                setFieldValue('email', emailOptionConfig || {})
                setFieldValue(
                  'firstName',
                  optionConfig?.option?.firstName || '',
                )
                setFieldValue('lastName', optionConfig?.option?.lastName || '')
              } else if (optionConfig === null) {
                // Allow clearing the field
                setFieldValue('mobile', '')
              }
            }}
            value={values.mobile}
            height="48px"
          />
        </MobileStyledFieldWrapper>
      </InputContainer>

      <InputContainer
        display="flex"
        flexDirection={['column', 'column', 'row']}
        gap={[3, 3, 4]}
      >
        <Box width={['100%', '100%', '50%']}>
          <CustomStyledFieldWrapper
            id="firstName"
            name="firstName"
            label="First Name"
          >
            <InputField
              name="firstName"
              inputProps={{
                placeholder: 'e.g. John',
                value: values.firstName,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('firstName', e.target.value),
              }}
            />
          </CustomStyledFieldWrapper>
        </Box>

        <Box width={['100%', '100%', '50%']} ml={[0, 0, 5]} mt={[7, 7, 0]}>
          <CustomStyledFieldWrapper
            id="lastName"
            name="lastName"
            label="Last Name"
          >
            <InputField
              name="lastName"
              inputProps={{
                placeholder: 'e.g. Doe',
                value: values.lastName,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('lastName', e.target.value),
              }}
            />
          </CustomStyledFieldWrapper>
        </Box>
      </InputContainer>

      <InputContainer>
        <CustomStyledFieldWrapper
          id="message"
          name="message"
          label="Message (optional)"
        >
          <TextArea
            name="message"
            placeholder="Add a message..."
            rows={4}
            containerProps={{}}
            value={values.message}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setFieldValue('message', e.target.value)
            }
          />
        </CustomStyledFieldWrapper>
      </InputContainer>
    </StepContainer>
  )

  const renderCurrentStep = (
    values: FormValues,
    _errors: any,
    _touched: any,
    setFieldValue: any,
  ) => {
    switch (currentStep) {
      case 1:
        return renderStep1(values, _errors, _touched, setFieldValue)
      case 2:
        return renderStep2(values, _errors, _touched, setFieldValue)
      case 3:
        return renderStep3(values, _errors, _touched, setFieldValue)
      default:
        return renderStep1(values, _errors, _touched, setFieldValue)
    }
  }

  const getPrimaryButtonLabel = () => {
    switch (currentStep) {
      case 3:
        return tenancyTransferMutation.isPending ? 'Sending...' : 'Send'
      default:
        return 'Next'
    }
  }

  const getSecondaryButtonLabel = () => {
    return currentStep === 1 ? 'Cancel' : 'Back'
  }

  const handlePrimaryAction = (submitForm: () => void, values: FormValues) => {
    if (currentStep === 3) {
      submitForm()
    } else {
      handleNext(values)
    }
  }

  const handleSecondaryAction = () => {
    if (currentStep === 1) {
      toggleModal()
    } else {
      handlePrevious()
    }
  }

  if (!isOpen) return null

  return (
    <Formik
      key={formKey}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={formValidation}
    >
      {({ values, errors, touched, setFieldValue, submitForm }) => {
        const modalBody = (
          <Form style={{ width: '100%' }}>
            <Box py={8}>
              <StyledProgressBar>
                <ProgressFill progress={getCurrentProgress()} />
              </StyledProgressBar>

              {renderCurrentStep(values, errors, touched, setFieldValue)}
            </Box>
          </Form>
        )

        return (
          <Modal
            modalHeading="Start Tenancy Transfer"
            modalBody={modalBody}
            toggleModal={toggleModal}
            primaryLabel={getPrimaryButtonLabel()}
            secondaryLabel={getSecondaryButtonLabel()}
            primaryAction={() => handlePrimaryAction(submitForm, values)}
            secondaryAction={handleSecondaryAction}
            showSecondaryButton={true}
            primaryButtonDisabled={
              tenancyTransferMutation.isPending ||
              hasCurrentStepErrors(values, currentStep)
            }
            modalFooter={undefined}
            takeDecreasedWidth
          />
        )
      }}
    </Formik>
  )
}

export default StartTenancyTransferModal
